import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { AppleSVG, GoogleSVG, OutlookSVG } from './SVG';

type Tone = 'personal' | 'work' | 'other' | 'busy' | 'open';

/** start and length are in hours from the first visible row. */
type DemoEvent = { start: number; length: number; label: string; tone: Tone };
type DemoColumn = { label: string; icon: JSX.Element; events: DemoEvent[] };

const SCENES = ['work_personal', 'clients', 'family', 'booking'] as const;
type Scene = typeof SCENES[number];

const HOURS = ['09:00', '10:00', '11:00', '12:00', '13:00'];
const ROW_HEIGHT = 36;
const ROTATE_MS = 7000;

const TONES: Record<Tone, React.CSSProperties> = {
    personal: { background: '#FDE9D9', color: '#7A3A10' },
    work: { background: 'var(--mantine-color-primary-0)', color: 'var(--mantine-color-primary-9)' },
    other: { background: '#E3F4E8', color: '#1E5631' },
    busy: {
        background: 'repeating-linear-gradient(135deg, var(--mantine-color-gray-2) 0 6px, transparent 6px 12px)',
        border: '1px solid var(--mantine-color-gray-5)',
        color: 'var(--mantine-color-dark-7)',
    },
    open: { border: '1px dashed #3A9A5B', color: '#1E5631' },
};

const icon = (Svg: typeof GoogleSVG) => <Svg width={14} height={14} />;

const useScenes = (): Record<Scene, DemoColumn[]> => {
    const { t } = useTranslation();
    const d = (key: string) => t(`home.hero.demo.${key}`);
    const busy = d('busy');
    return {
        work_personal: [
            { label: d('work_personal.a'), icon: icon(AppleSVG), events: [{ start: 2, length: 2, label: d('work_personal.a_event'), tone: 'personal' }] },
            {
                label: d('work_personal.b'), icon: icon(OutlookSVG), events: [
                    { start: 0, length: 2, label: d('work_personal.b_event'), tone: 'work' },
                    { start: 2, length: 2, label: busy, tone: 'busy' },
                ]
            },
        ],
        clients: [
            {
                label: d('clients.a'), icon: icon(GoogleSVG), events: [
                    { start: 0, length: 1.5, label: d('clients.a_event'), tone: 'work' },
                    { start: 3, length: 1.5, label: busy, tone: 'busy' },
                ]
            },
            {
                label: d('clients.b'), icon: icon(OutlookSVG), events: [
                    { start: 0, length: 1.5, label: busy, tone: 'busy' },
                    { start: 3, length: 1.5, label: d('clients.b_event'), tone: 'other' },
                ]
            },
        ],
        family: [
            { label: d('family.a'), icon: icon(GoogleSVG), events: [{ start: 0.5, length: 1.5, label: d('family.a_event'), tone: 'personal' }] },
            { label: d('family.b'), icon: icon(AppleSVG), events: [{ start: 3, length: 1.5, label: d('family.b_event'), tone: 'other' }] },
            {
                label: d('family.c'), icon: icon(GoogleSVG), events: [
                    { start: 0.5, length: 1.5, label: d('family.a_copy'), tone: 'personal' },
                    { start: 3, length: 1.5, label: d('family.b_copy'), tone: 'other' },
                ]
            },
        ],
        booking: [
            { label: d('booking.a'), icon: icon(OutlookSVG), events: [{ start: 0, length: 1.5, label: d('booking.a_event'), tone: 'work' }] },
            { label: d('booking.b'), icon: icon(AppleSVG), events: [{ start: 2.5, length: 1.5, label: d('booking.b_event'), tone: 'personal' }] },
            {
                label: d('booking.c'), icon: icon(GoogleSVG), events: [
                    { start: 0, length: 1.5, label: busy, tone: 'busy' },
                    { start: 1.5, length: 1, label: d('open'), tone: 'open' },
                    { start: 2.5, length: 1.5, label: busy, tone: 'busy' },
                    { start: 4, length: 1, label: d('open'), tone: 'open' },
                ]
            },
        ],
    };
};

const CalendarDemo = ({ columns }: { columns: DemoColumn[] }) => (
    <Box style={{ display: 'grid', gridTemplateColumns: `44px repeat(${columns.length}, minmax(0, 1fr))`, columnGap: 8, rowGap: 8 }}>
        <div />
        {columns.map((column) => (
            <Group key={column.label} gap={6} wrap="nowrap">
                {column.icon}
                <Text fw={600} size="sm" truncate>{column.label}</Text>
            </Group>
        ))}
        <div>
            {HOURS.map((hour) => (
                <Text key={hour} size="xs" c="dimmed" h={ROW_HEIGHT} style={{ fontVariantNumeric: 'tabular-nums' }}>{hour}</Text>
            ))}
        </div>
        {columns.map((column) => (
            <Box key={column.label} pos="relative" h={ROW_HEIGHT * HOURS.length} style={{ borderLeft: '1px dashed var(--mantine-color-gray-4)' }}>
                {column.events.map((event, idx) => (
                    <Box
                        key={idx}
                        pos="absolute"
                        left={4}
                        right={4}
                        top={event.start * ROW_HEIGHT + 2}
                        h={event.length * ROW_HEIGHT - 4}
                        px={8}
                        py={4}
                        style={{ borderRadius: 6, fontSize: 13, lineHeight: 1.25, overflow: 'hidden', ...TONES[event.tone] }}
                        dangerouslySetInnerHTML={{ __html: event.label }}
                    />
                ))}
            </Box>
        ))}
    </Box>
);

/** Rotating examples of what syncing looks like for different kinds of users. */
export const HeroUseCases = () => {
    const { t } = useTranslation();
    const scenes = useScenes();
    const [active, setActive] = useState<Scene>(SCENES[0]);
    const [paused, setPaused] = useState(false);
    // Once someone picks a tab themselves, stop rotating away from it.
    const [userPicked, setUserPicked] = useState(false);
    // Only animate scene changes, so the first scene is visible as soon as the page renders.
    const [changed, setChanged] = useState(false);

    useEffect(() => {
        if (paused || userPicked) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const timer = window.setTimeout(() => {
            setActive((current) => SCENES[(SCENES.indexOf(current) + 1) % SCENES.length]);
            setChanged(true);
        }, ROTATE_MS);
        return () => window.clearTimeout(timer);
    }, [active, paused, userPicked]);

    return (
        <Stack gap="md" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-label={t('home.hero.demo.aria')}>
            <Group gap={8} role="tablist" justify="center">
                {SCENES.map((scene) => {
                    const selected = scene === active;
                    return (
                        <UnstyledButton
                            key={scene}
                            role="tab"
                            aria-selected={selected}
                            className={`hero-usecase-tab${selected ? ' active' : ''}`}
                            onClick={() => { setActive(scene); setUserPicked(true); setChanged(true); }}
                        >
                            {t(`home.hero.demo.${scene}.tab`)}
                            {selected && !userPicked && <span key={scene} className="hero-usecase-progress" style={{ animationDuration: `${ROTATE_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }} />}
                        </UnstyledButton>
                    );
                })}
            </Group>
            <Paper withBorder radius="md" p="lg" shadow="md" role="tabpanel">
                <div key={active} className={changed ? 'hero-usecase-fade' : undefined}>
                    <CalendarDemo columns={scenes[active]} />
                    <Text size="sm" c="dimmed" mt="md" mih={42}>
                        <Text span fw={600} c="primary.6">{t(`home.hero.demo.${active}.rule`)}</Text> {t(`home.hero.demo.${active}.caption`)}
                    </Text>
                </div>
            </Paper>
        </Stack>
    );
};
